/**
 * @file: RewardsCard.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { Typography } from '@euler/app/components/typography';
import { MeIcon, MeIconName } from '@euler/app/flows/me/components/MeIcon';
import {
  SectionButton,
  SectionCard,
} from '@euler/app/flows/me/components/SectionCard';
import { MK } from '@euler/app/flows/me/components/meKit';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrgMemberAccount } from '@euler/model/entity';
import { formatMoney } from '@euler/utils/formatMoney';
import { memo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

export type RewardsLink = {
  key: string;
  label: string;
  icon: MeIconName;
  onPress: () => void;
};

export const RewardsCard = memo(
  ({
    account,
    links,
    onWithdraw,
  }: {
    account: OrgMemberAccount | undefined;
    links: RewardsLink[];
    onWithdraw: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const canWithdraw = account != null && account.balance > 0;
    const breakdown: {
      label: string;
      value: number | undefined;
      icon: MeIconName;
      color: string;
    }[] = [
      {
        label: '推荐奖励',
        value: account?.referringCommissionFeeBalance,
        icon: 'user-plus',
        color: MK.brand,
      },
      {
        label: '施工奖励',
        value: account?.adoptionCommissionFeeBalance,
        icon: 'wrench',
        color: MK.success,
      },
      {
        label: '成交奖励',
        value: account?.closingCommissionFeeBalance,
        icon: 'cart-3',
        color: MK.warn,
      },
      {
        label: '活动奖励',
        value: account?.campaignRewardBalance,
        icon: 'star',
        color: MK.purple,
      },
    ];
    const totalIssued =
      account != null
        ? account.referringCommissionFeeTotal +
          account.adoptionCommissionFeeTotal +
          account.closingCommissionFeeTotal +
          account.campaignRewardTotal
        : undefined;

    return (
      <SectionCard
        title="我的奖励"
        icon="wallet-money"
        action={
          canWithdraw ? (
            <SectionButton label="提现" onPress={onWithdraw} />
          ) : null
        }
      >
        <View
          style={{
            marginTop: rpx(12),
            borderRadius: rpx(12),
            backgroundColor: MK.successSoft,
            padding: rpx(16),
          }}
        >
          <Typography.Text size={rpx(12)} color={MK.success}>
            可提现奖励
          </Typography.Text>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: rpx(8),
              marginTop: rpx(2),
            }}
          >
            <Typography.Text
              size={rpx(28)}
              lineHeight={rpx(36)}
              color={MK.success}
              bold
            >
              {formatMoney(account?.balance, { prefix: true })}
            </Typography.Text>
            <Typography.Text
              size={rpx(12)}
              color={MK.success}
              style={{ marginBottom: rpx(6) }}
            >
              累计已发放 {formatMoney(totalIssued, { prefix: true })}
            </Typography.Text>
          </View>
        </View>
        <View
          style={{
            marginTop: rpx(12),
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            rowGap: rpx(10),
          }}
        >
          {breakdown.map(item => (
            <View
              key={item.label}
              style={{
                width: '48.5%',
                flexDirection: 'row',
                alignItems: 'center',
                borderRadius: rpx(12),
                backgroundColor: MK.muted,
                padding: rpx(12),
              }}
            >
              <View
                style={{
                  width: rpx(32),
                  height: rpx(32),
                  borderRadius: rpx(8),
                  backgroundColor: MK.surface,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MeIcon name={item.icon} size={rpx(16)} color={item.color} />
              </View>
              <View style={{ flex: 1, minWidth: 0, marginLeft: rpx(10) }}>
                <Typography.Text
                  size={rpx(12)}
                  color={MK.mutedInk}
                  numberOfLines={1}
                >
                  {item.label}
                </Typography.Text>
                <Typography.Text
                  size={rpx(15)}
                  color={MK.ink}
                  bold
                  numberOfLines={1}
                >
                  {formatMoney(item.value, { prefix: true })}
                </Typography.Text>
              </View>
            </View>
          ))}
        </View>
        {links.length > 0 && (
          <View style={{ marginTop: rpx(8) }}>
            {links.map(link => (
              <TouchableOpacity
                key={link.key}
                onPress={link.onPress}
                activeOpacity={0.6}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  borderTopWidth: StyleSheet.hairlineWidth,
                  borderTopColor: MK.hairline,
                  paddingVertical: rpx(14),
                }}
              >
                <MeIcon name={link.icon} size={rpx(18)} color={MK.brand} />
                <Typography.Text
                  size={rpx(14)}
                  color={MK.ink}
                  ml={rpx(12)}
                  style={{ flex: 1 }}
                >
                  {link.label}
                </Typography.Text>
                <MeIcon
                  name="alt-arrow-right"
                  size={rpx(16)}
                  color={MK.mutedInk}
                />
              </TouchableOpacity>
            ))}
          </View>
        )}
      </SectionCard>
    );
  },
);
